// src/hooks/usePersistentAsrDraft.ts
import { useCallback, useEffect, useState } from "react";
import { buildAsrDraftKey } from "../lib/asrDraftStorage";

interface UsePersistentAsrDraftOptions {
    projectId: string | null | undefined;
    flagId: string | null | undefined;
    initialText?: string; // e.g. AI-generated first draft
}

export function usePersistentAsrDraft({
    projectId,
    flagId,
    initialText = "",
}: UsePersistentAsrDraftOptions) {
    const [text, setText] = useState(initialText);
    const [loaded, setLoaded] = useState(false);

    // Load from localStorage on mount / when IDs change
    useEffect(() => {
        if (!projectId || !flagId) return;
        if (typeof window === "undefined") return;

        const key = buildAsrDraftKey(projectId, flagId);
        const stored = window.localStorage.getItem(key);

        if (stored !== null) {
            setText(stored);
        } else if (initialText) {
            // seed storage with initialText once
            window.localStorage.setItem(key, initialText);
            setText(initialText);
        }

        setLoaded(true);
    }, [projectId, flagId, initialText]);

    const persist = useCallback(
        (value: string) => {
            setText(value);
            if (!projectId || !flagId) return;
            if (typeof window === "undefined") return;

            const key = buildAsrDraftKey(projectId, flagId);
            window.localStorage.setItem(key, value);
        },
        [projectId, flagId]
    );

    const clear = useCallback(() => {
        if (!projectId || !flagId) return;
        if (typeof window === "undefined") return;

        const key = buildAsrDraftKey(projectId, flagId);
        window.localStorage.removeItem(key);
        setText("");
    }, [projectId, flagId]);

    return {
        text,
        setText: persist,
        clear,
        loaded,
    };
}
